<template>
  <div>
    <v-card :disabled="isProcessing" :loading="isProcessing">
      <template v-slot:progress>
        <v-progress-linear absolute color="success" height="4" indeterminate></v-progress-linear>
      </template>
      <v-tabs v-model="tab" class="stock-color" left dark icons-and-text>
        <v-tabs-slider></v-tabs-slider>
        <v-tab href="#tab-1">
          <v-toolbar-title>{{ $t("STOCK.MOVEMENTS.TITLE") }}</v-toolbar-title>
        </v-tab>
        <v-spacer></v-spacer>
        <div class="buttons-tab">
          <v-btn
            v-if="hasPermissions('stock-receipts-create')"
            class="mr-2 success"
            :disabled="isProcessing"
            @click="$router.push({ name: 'stock-receipts-create' })"
          >
            {{ $t("STOCK.MOVEMENTS.NEW_RECEIPT") }}
          </v-btn>
          <v-btn
            v-if="hasPermissions('stock-receipts-create')"
            class="mr-2 teal darken-1"
            :disabled="isProcessing"
            @click="$router.push({ name: 'stock-receipts-multi-po-create' })"
          >
            {{ $t("STOCK.MOVEMENTS.NEW_MULTI_PO_RECEIPT") }}
          </v-btn>
          <v-btn
            v-if="hasPermissions('stock-sales-create')"
            class="mr-2 primary"
            :disabled="isProcessing"
            @click="$router.push({ name: 'stock-sales-create' })"
          >
            {{ $t("STOCK.MOVEMENTS.NEW_DELIVERY") }}
          </v-btn>
          <v-btn
            v-if="hasPermissions('stock-write-offs-create')"
            class="mr-4 deep-orange darken-1"
            :disabled="isProcessing"
            @click="$router.push({ name: 'stock-write-offs-create' })"
          >
            {{ $t("STOCK.MOVEMENTS.NEW_WRITE_OFF") }}
          </v-btn>
          <v-btn
            v-if="hasPermissions('stock-transfers-create')"
            class="mr-4 indigo darken-1"
            :disabled="isProcessing"
            @click="$router.push({ name: 'stock-transfers-create' })"
          >
            {{ $t("STOCK.MOVEMENTS.NEW_TRANSFER") }}
          </v-btn>
        </div>
      </v-tabs>
      <v-tabs-items v-model="tab">
        <v-tab-item value="tab-1">
          <v-card flat>
            <v-card-text v-if="summaryTotals.length" class="pb-0 pt-3">
              <div class="caption grey--text text--darken-1 mb-1">
                {{ $t("STOCK.MOVEMENTS.SUMMARY") }}
              </div>
              <div class="d-flex flex-wrap">
                <v-chip
                  v-for="total in summaryTotals"
                  :key="`${total.organization_id}-${total.currency_code}`"
                  small
                  class="mr-2 mb-2"
                  color="grey lighten-3"
                >
                  <span class="font-weight-medium mr-1">{{ total.organization_code }}:</span>
                  {{ formatSignedMoney(total.signed_inventory_value_net, total.currency_code) }}
                </v-chip>
              </div>
            </v-card-text>
            <v-card-text
              v-if="hasPermissions('stock-invoices-create')"
              class="pb-0 pt-3"
            >
              <v-alert type="info" dense outlined class="mb-0">
                {{ $t("STOCK.MOVEMENTS.INVOICE_SELECTION_HINT") }}
              </v-alert>
            </v-card-text>
            <v-card-text v-if="selectedActionIds.length && hasPermissions('stock-invoices-create')" class="pb-0 pt-3">
              <v-alert type="info" dense outlined class="mb-0 d-flex align-center">
                <span class="mr-4">
                  {{
                    selectedSelectionKind === "receipt"
                      ? $t("STOCK.INVOICES.BULK_REGISTER_SUPPLIER_INVOICE_HINT", {
                          count: selectedActionIds.length,
                        })
                      : $t("STOCK.INVOICES.BULK_CREATE_HINT", { count: selectedActionIds.length })
                  }}
                </span>
                <v-spacer></v-spacer>
                <v-btn
                  small
                  color="primary"
                  :loading="isCreatingInvoice"
                  @click="openBulkInvoiceDialog"
                >
                  {{
                    selectedSelectionKind === "receipt"
                      ? $t("STOCK.INVOICES.BULK_REGISTER_SUPPLIER_INVOICE")
                      : $t("STOCK.INVOICES.BULK_CREATE_INVOICE")
                  }}
                </v-btn>
                <v-btn small text class="ml-2" @click="clearSelection">
                  {{ $t("GENERAL.LABELS.CANCEL") }}
                </v-btn>
              </v-alert>
            </v-card-text>
            <v-card-text class="pa-0">
              <Table
                v-if="userStatePrepared"
                ref="stock-movements-table"
                height="500"
                name="stock-movements-table"
                :headers="headers"
                :blink-row-item-key="blinkRowItemKey"
                :server-data-options="{ method: tableItemsMethod }"
                :items-per-page="25"
                :loading="isProcessing"
                :filters="tableFilters"
                :selectable="hasPermissions('stock-invoices-create')"
                :row-selectable="isInvoiceSelectable"
                dense
                fixed-header
                filterable
                @item-selected="handleMovementSelected"
              >
                <template
                  v-for="(header, index) in headers"
                  v-slot:[`item.${header.value}`]="{ item }"
                >
                  <template v-if="header.value === 'movement_direction'">
                    <v-chip
                      :key="index"
                      small
                      :color="directionColor(item.item)"
                      dark
                      class="font-weight-bold"
                    >
                      <v-icon left small>{{ directionIcon(item.item) }}</v-icon>
                      {{ directionSign(item.item) }}
                    </v-chip>
                  </template>
                  <template v-else-if="header.value === 'action_type.name'">
                    <div :key="index" class="text-center">
                      <div>{{ actionTypeName(item.item) }}</div>
                      <div
                        v-if="item.item.action_type && item.item.action_type.category"
                        class="caption grey--text text--darken-1"
                      >
                        {{ actionCategoryLabel(item.item.action_type.category) }}
                      </div>
                    </div>
                  </template>
                  <template v-else-if="header.value === 'code'">
                    <div :key="index">
                      <router-link :to="movementViewRoute(item.item)">
                        {{ item.item.code }}
                      </router-link>
                      <div
                        v-if="item.item.parent_action"
                        class="caption grey--text text--darken-1"
                      >
                        {{ $t("STOCK.RECEIPTS.PARENT_DOCUMENT") }}:
                        <router-link :to="movementViewRoute(item.item.parent_action)">
                          {{ item.item.parent_action.code }}
                        </router-link>
                      </div>
                    </div>
                  </template>
                  <template v-else-if="header.value === 'status'">
                    <v-chip :key="index" small :color="statusColor(item.item.status)" dark>
                      {{ statusLabel(item.item.status) }}
                    </v-chip>
                  </template>
                  <template v-else-if="header.value === 'linked_invoice'">
                    <span :key="index">
                      <router-link
                        v-if="item.item.linked_invoice"
                        :to="{
                          name: 'stock-invoices-view',
                          params: { id: item.item.linked_invoice.id },
                        }"
                      >
                        {{ linkedInvoiceLabel(item.item.linked_invoice) }}
                      </router-link>
                      <template v-else>-</template>
                    </span>
                  </template>
                  <template v-else-if="header.value === 'purchase_orders'">
                    <span :key="index">
                      <template v-if="purchaseOrders(item.item).length">
                        <span
                          v-for="(po, poIndex) in purchaseOrders(item.item)"
                          :key="po.id"
                        >
                          <router-link
                            :to="{ name: 'stock-purchase-orders-view', params: { id: po.id } }"
                          >
                            {{ po.code }}
                          </router-link>
                          <span v-if="poIndex < purchaseOrders(item.item).length - 1">, </span>
                        </span>
                      </template>
                      <template v-else>-</template>
                    </span>
                  </template>
                  <template v-else-if="header.value === 'sales_orders'">
                    <span :key="index">
                      <template v-if="salesOrders(item.item).length">
                        <span
                          v-for="(so, soIndex) in salesOrders(item.item)"
                          :key="so.id"
                        >
                          <router-link
                            :to="{ name: 'stock-sales-orders-view', params: { id: so.id } }"
                          >
                            {{ so.code }}
                          </router-link>
                          <span v-if="soIndex < salesOrders(item.item).length - 1">, </span>
                        </span>
                      </template>
                      <template v-else>-</template>
                    </span>
                  </template>
                  <template v-else-if="header.value === 'deal.label'">
                    {{ dealLabel(item.item) }}
                  </template>
                  <template v-else-if="header.value === 'counterparty.label'">
                    {{ counterpartyLabel(item.item) }}
                  </template>
                  <template v-else-if="header.value === 'warehouse.label'">
                    {{ warehouseLabel(item.item) }}
                  </template>
                  <template v-else-if="header.value === 'organization.name'">
                    {{ organizationLabel(item.item) }}
                  </template>
                  <template v-else-if="header.value === 'supplier_invoice_no'">
                    {{ item.item.supplier_invoice_no || "-" }}
                  </template>
                  <template v-else-if="header.value === 'items_count'">
                    {{ item.item.items_count != null ? item.item.items_count : "-" }}
                  </template>
                  <template v-else-if="header.value === 'total_net'">
                    {{ formatSignedDocumentNet(item.item) }}
                  </template>
                  <template v-else-if="header.value === 'total_inventory_cost_net'">
                    {{ formatFinancialValue(
                      item.item.total_inventory_cost_net,
                      item.item.currency_code
                    ) }}
                  </template>
                  <template v-else-if="header.value === 'total_cost_of_goods_net'">
                    {{ formatFinancialValue(
                      item.item.total_cost_of_goods_net,
                      item.item.currency_code
                    ) }}
                  </template>
                  <template v-else-if="header.value === 'total_margin_net'">
                    <span
                      v-if="isOutboundMovement(item.item)"
                      :class="marginTextClass(item.item.total_margin_net)"
                    >
                      {{ formatFinancialValue(
                        item.item.total_margin_net,
                        item.item.currency_code
                      ) }}
                    </span>
                    <span v-else :key="`${index}-margin-net`">-</span>
                  </template>
                  <template v-else-if="header.value === 'margin_pct'">
                    <span
                      v-if="isOutboundMovement(item.item)"
                      :class="marginTextClass(item.item.total_margin_net)"
                    >
                      {{ movementMarginPct(item.item) }}
                    </span>
                    <span v-else :key="`${index}-margin-pct`">-</span>
                  </template>
                  <template v-else>
                    {{ item.value || "-" }}
                  </template>
                </template>
              </Table>
            </v-card-text>
          </v-card>
        </v-tab-item>
      </v-tabs-items>
    </v-card>

    <StockInvoiceLinkDialog
      v-model="showInvoiceLinkDialog"
      :action-ids="selectedActionIds"
      :kind="selectedSelectionKind === 'receipt' ? 'receipt' : 'delivery'"
      :title="invoiceLinkDialogTitle"
      :hint="invoiceLinkDialogHint"
      :show-pass-through="selectedSelectionKind === 'delivery'"
      :submitting="isCreatingInvoice"
      @confirm="handleBulkInvoiceLinkConfirm"
      @error="handleInvoiceLinkError"
    />
  </div>
</template>

<script>
import Table from "@/views/partials/v-table/Table.vue";
import userStateService from "@/common/userState.service";
import stockMovementRowDisplayMixin from "@/views/partials/stock/stockMovementRowDisplay.mixin";
import { GET_STOCK_MOVEMENTS } from "@/store/stock/stockMovements.module";
import {
  GET_STOCK_DEAL_OPTIONS,
  GET_STOCK_ACTION_TYPE_OPTIONS,
} from "@/store/stock/stockReceipts.module";
import StockInvoiceLinkDialog from "@/views/partials/stock/invoices/StockInvoiceLinkDialog.vue";
import { CREATE_STOCK_INVOICE_FROM_DELIVERIES, CREATE_STOCK_INVOICE_FROM_RECEIPTS } from "@/store/stock/stockInvoices.module";
import { SHOW_SNACK } from "@/store/snack.module";

export default {
  name: "StockListMovements",
  components: { Table, StockInvoiceLinkDialog },
  mixins: [stockMovementRowDisplayMixin],
  data() {
    return {
      tableName: "stock-movements-table",
      blinkRowItemKey: null,
      userStatePrepared: false,
      tab: "tab-1",
      isProcessing: false,
      isCreatingInvoice: false,
      selectedActionIds: [],
      selectedSelectionKind: null,
      showInvoiceLinkDialog: false,
      tableItemsMethod: GET_STOCK_MOVEMENTS,
      tableFilters: [],
      headers: [
        {
          text: this.$t("STOCK.RECEIPTS.DIRECTION"),
          value: "movement_direction",
          sortable: false,
          align: "center",
          width: "5%",
        },
        {
          text: this.$t("STOCK.RECEIPTS.MOVEMENT_TYPE"),
          value: "action_type.name",
          sortable: false,
          align: "center",
          width: "10%",
        },
        {
          text: this.$t("STOCK.RECEIPTS.DOCUMENT_CODE"),
          value: "code",
          sortable: true,
          align: "center",
          width: "9%",
        },
        {
          text: this.$t("GENERAL.LABELS.STATUS"),
          value: "status",
          sortable: true,
          align: "center",
          width: "7%",
        },
        {
          text: this.$t("STOCK.MOVEMENTS.INVOICE"),
          value: "linked_invoice",
          sortable: false,
          align: "center",
          width: "8%",
        },
        {
          text: this.$t("STOCK.RECEIPTS.PURCHASE_ORDER"),
          value: "purchase_orders",
          sortable: false,
          align: "center",
          width: "8%",
        },
        {
          text: this.$t("STOCK.MOVEMENTS.SALES_ORDER"),
          value: "sales_orders",
          sortable: false,
          align: "center",
          width: "8%",
        },
        {
          text: this.$t("STOCK.PURCHASE_ORDERS.DEAL"),
          value: "deal.label",
          sortable: false,
          align: "center",
          width: "9%",
        },
        {
          text: this.$t("STOCK.RECEIPTS.COUNTERPARTY"),
          value: "counterparty.label",
          sortable: false,
          align: "center",
          width: "9%",
        },
        {
          text: this.$t("STOCK.RECEIPTS.WAREHOUSE"),
          value: "warehouse.label",
          sortable: false,
          align: "center",
          width: "9%",
        },
        {
          text: this.$t("STOCK.WAREHOUSES.ORGANIZATION"),
          value: "organization.name",
          sortable: false,
          align: "center",
          width: "7%",
        },
        {
          text: this.$t("STOCK.RECEIPTS.DATE"),
          value: "action_date",
          sortable: true,
          align: "center",
          width: "7%",
        },
        {
          text: this.$t("STOCK.RECEIPTS.LINES"),
          value: "items_count",
          sortable: false,
          align: "center",
          width: "4%",
        },
        {
          text: this.$t("STOCK.MOVEMENTS.TOTAL_NET"),
          value: "total_net",
          sortable: false,
          align: "center",
          width: "7%",
        },
        {
          text: this.$t("STOCK.MOVEMENTS.INVENTORY_COST"),
          value: "total_inventory_cost_net",
          sortable: false,
          align: "center",
          width: "7%",
        },
        {
          text: this.$t("STOCK.MOVEMENTS.TOTAL_COGS"),
          value: "total_cost_of_goods_net",
          sortable: false,
          align: "center",
          width: "7%",
        },
        {
          text: this.$t("STOCK.MOVEMENTS.TOTAL_MARGIN"),
          value: "total_margin_net",
          sortable: false,
          align: "center",
          width: "7%",
        },
        {
          text: this.$t("STOCK.MOVEMENTS.MARGIN_PCT"),
          value: "margin_pct",
          sortable: false,
          align: "center",
          width: "5%",
        },
      ],
    };
  },
  computed: {
    stockContextVersion() {
      return this.$store.getters.stockContextVersion;
    },
    summaryTotals() {
      return this.$store.getters.stockMovementsSummary?.totals_by_organization || [];
    },
    invoiceLinkDialogTitle() {
      return this.selectedSelectionKind === "receipt"
        ? this.$t("STOCK.INVOICES.BULK_REGISTER_SUPPLIER_INVOICE")
        : this.$t("STOCK.INVOICES.BULK_CREATE_INVOICE");
    },
    invoiceLinkDialogHint() {
      return this.selectedSelectionKind === "receipt"
        ? this.$t("STOCK.INVOICES.BULK_REGISTER_SUPPLIER_INVOICE_HINT", {
            count: this.selectedActionIds.length,
          })
        : this.$t("STOCK.INVOICES.BULK_CREATE_HINT", { count: this.selectedActionIds.length });
    },
  },
  watch: {
    stockContextVersion() {
      this.loadTableFilters().finally(() => this.reloadTable());
    },
  },
  mounted() {
    this.blinkRowItemKey = userStateService.getState(
      `table-state.${this.tableName}.blinkRowItemKey`,
      null
    );
    this.loadTableFilters().finally(() => {
      this.userStatePrepared = true;
    });
  },
  methods: {
    movementViewRoute(row) {
      const category = row.action_type && row.action_type.category;
      if (category === "sale") {
        return { name: "stock-sales-view", params: { id: row.id } };
      }
      if (category === "write_off") {
        return { name: "stock-write-offs-view", params: { id: row.id } };
      }
      if (category === "transfer") {
        return { name: "stock-transfers-view", params: { id: row.id } };
      }
      return { name: "stock-receipts-view", params: { id: row.id } };
    },
    purchaseOrders(row) {
      return row.purchase_orders || [];
    },
    salesOrders(row) {
      return row.sales_orders || [];
    },
    directionValue(row) {
      if (row.movement_direction != null) {
        return Number(row.movement_direction);
      }
      if (row.action_type && row.action_type.direction != null) {
        return Number(row.action_type.direction);
      }
      return 0;
    },
    directionSign(row) {
      const direction = this.directionValue(row);
      if (direction > 0) {
        return "+";
      }
      if (direction < 0) {
        return "−";
      }
      return "±";
    },
    directionColor(row) {
      const direction = this.directionValue(row);
      if (direction > 0) {
        return "success";
      }
      if (direction < 0) {
        return "error";
      }
      return "grey darken-1";
    },
    directionIcon(row) {
      const direction = this.directionValue(row);
      if (direction > 0) {
        return "mdi-arrow-down-bold";
      }
      if (direction < 0) {
        return "mdi-arrow-up-bold";
      }
      return "mdi-swap-horizontal";
    },
    actionTypeName(row) {
      return row.action_type ? row.action_type.name : "-";
    },
    actionCategoryLabel(category) {
      const key = `STOCK.RECEIPTS.ACTION_CATEGORIES.${category}`;
      return this.$te(key) ? this.$t(key) : category;
    },
    counterpartyLabel(row) {
      const company = row.source_company || row.destination_company;
      if (!company) {
        return "-";
      }
      return company.label || company.name || "-";
    },
    organizationLabel(row) {
      const org = row.organization;
      if (!org) {
        return "-";
      }
      return org.name || org.code || "-";
    },
    warehouseLabel(row) {
      const direction = this.directionValue(row);
      const warehouse =
        direction < 0 ? row.source_warehouse : row.destination_warehouse;
      const fallback = row.destination_warehouse || row.source_warehouse;
      const wh = warehouse || fallback;
      if (!wh) {
        return "-";
      }
      if (wh.code && wh.name) {
        return `${wh.code} — ${wh.name}`;
      }
      return wh.name || wh.code || "-";
    },
    dealLabel(row) {
      const deal = row.deal;
      if (!deal) {
        return "-";
      }
      if (deal.code && deal.name) {
        return `${deal.code} — ${deal.name}`;
      }
      return deal.name || deal.code || "-";
    },
    signedDocumentNet(row) {
      const direction = this.directionValue(row);
      const amount = Number(row.total_net || 0);
      if (direction < 0) {
        return -amount;
      }
      return amount;
    },
    formatSignedDocumentNet(row) {
      return this.formatSignedMoney(this.signedDocumentNet(row), row.currency_code);
    },
    marginTextClass(value) {
      if (value == null || Number(value) === 0) {
        return "";
      }
      return Number(value) > 0 ? "success--text" : "error--text";
    },
    formatSignedMoney(value, currency) {
      const amount = Number(value || 0);
      const prefix = amount > 0 ? "+" : "";
      return `${prefix}${amount.toFixed(2)} ${currency || ""}`.trim();
    },
    loadTableFilters() {
      const statusItems = ["draft", "expected", "finalized", "reversed", "cancelled"].map(
        (status) => ({
          id: status,
          label: this.$te(`STOCK.STATUSES.RECEIPT.${status.toUpperCase()}`)
            ? this.$t(`STOCK.STATUSES.RECEIPT.${status.toUpperCase()}`)
            : status,
        })
      );

      const directionItems = [
        { id: 1, label: this.$t("STOCK.RECEIPTS.DIRECTION_IN") },
        { id: -1, label: this.$t("STOCK.RECEIPTS.DIRECTION_OUT") },
        { id: 0, label: this.$t("STOCK.RECEIPTS.DIRECTION_NEUTRAL") },
      ];

      const categoryItems = [
        "receipt",
        "sale",
        "transfer",
        "write_off",
        "production",
        "adjustment",
      ].map((category) => ({
        id: category,
        label: this.actionCategoryLabel(category),
      }));

      const hasPoItems = [
        { id: "1", label: this.$t("STOCK.RECEIPTS.HAS_PO_YES") },
        { id: "0", label: this.$t("STOCK.RECEIPTS.HAS_PO_NO") },
      ];

      const hasSoItems = [
        { id: "1", label: this.$t("STOCK.MOVEMENTS.HAS_SO_YES") },
        { id: "0", label: this.$t("STOCK.MOVEMENTS.HAS_SO_NO") },
      ];

      const organizationIds = this.$store.getters.stockContextOrganizationIds;
      const dealParams = { limit: 100 };
      if (organizationIds.length === 1) {
        dealParams.organization_id = organizationIds[0];
      } else if (organizationIds.length > 1) {
        dealParams.organization_ids = organizationIds;
      }

      return Promise.all([
        this.$store.dispatch(GET_STOCK_ACTION_TYPE_OPTIONS),
        this.$store.dispatch(GET_STOCK_DEAL_OPTIONS, dealParams),
      ])
        .then(([actionTypes, deals]) => {
          const actionTypeItems = (actionTypes || []).map((type) => ({
            id: type.id,
            label: type.label || type.name,
          }));

          const dealItems = (deals || []).map((deal) => ({
            id: deal.id,
            label: deal.code ? `${deal.code} — ${deal.name}` : deal.name,
          }));

          this.tableFilters = [
            {
              key: "direction",
              label: this.$t("STOCK.RECEIPTS.DIRECTION"),
              type: "select",
              "item-value": "id",
              "item-text": "label",
              items: directionItems,
            },
            {
              key: "action_type_id",
              label: this.$t("STOCK.RECEIPTS.MOVEMENT_TYPE"),
              type: "select",
              "item-value": "id",
              "item-text": "label",
              items: actionTypeItems,
            },
            {
              key: "action_category",
              label: this.$t("STOCK.RECEIPTS.ACTION_CATEGORY"),
              type: "select",
              "item-value": "id",
              "item-text": "label",
              items: categoryItems,
            },
            {
              key: "status",
              label: this.$t("GENERAL.LABELS.STATUS"),
              type: "select",
              "item-value": "id",
              "item-text": "label",
              items: statusItems,
            },
            {
              key: "date_from",
              label: `${this.$t("STOCK.RECEIPTS.DATE")} (${this.$t("GENERAL.LABELS.START_DATE")})`,
              type: "date",
            },
            {
              key: "date_to",
              label: `${this.$t("STOCK.RECEIPTS.DATE")} (${this.$t("GENERAL.LABELS.END_DATE")})`,
              type: "date",
            },
            {
              key: "deal_id",
              label: this.$t("STOCK.PURCHASE_ORDERS.DEAL"),
              type: "select",
              "item-value": "id",
              "item-text": "label",
              items: dealItems,
            },
            {
              key: "has_purchase_order",
              label: this.$t("STOCK.RECEIPTS.HAS_PO"),
              type: "select",
              "item-value": "id",
              "item-text": "label",
              items: hasPoItems,
            },
            {
              key: "has_sales_order",
              label: this.$t("STOCK.MOVEMENTS.HAS_SO"),
              type: "select",
              "item-value": "id",
              "item-text": "label",
              items: hasSoItems,
            },
          ];
        })
        .catch(() => {
          this.tableFilters = [
            {
              key: "status",
              label: this.$t("GENERAL.LABELS.STATUS"),
              type: "select",
              "item-value": "id",
              "item-text": "label",
              items: statusItems,
            },
          ];
        });
    },
    reloadTable() {
      this.$nextTick(() => {
        this.$refs["stock-movements-table"]?.fetchServerData();
      });
    },
    isSaleDelivery(row) {
      const category = row.action_type && row.action_type.category;
      const slug = row.action_type && row.action_type.slug;
      return (
        row.status === "finalized" &&
        (category === "sale" || (slug && slug.startsWith("sale_")))
      );
    },
    isReceipt(row) {
      const category = row.action_type && row.action_type.category;
      const slug = row.action_type && row.action_type.slug;
      return (
        row.status === "finalized" &&
        (category === "receipt" ||
          (slug &&
            (slug.startsWith("receipt_") || slug === "intercompany_receipt")))
      );
    },
    invoiceSelectionKind(row) {
      if (this.isSaleDelivery(row)) {
        return "delivery";
      }
      if (this.isReceipt(row)) {
        return "receipt";
      }
      return null;
    },
    isInvoiceSelectable(row) {
      if (row.is_on_issued_invoice || row.is_on_draft_invoice) {
        return false;
      }

      return this.invoiceSelectionKind(row) !== null;
    },
    handleMovementSelected(item) {
      const kind = this.invoiceSelectionKind(item);
      if (!kind) {
        return;
      }

      const id = Number(item.id);
      const isSelected = this.selectedActionIds.includes(id);

      if (!isSelected) {
        if (this.selectedSelectionKind && this.selectedSelectionKind !== kind) {
          this.$store.dispatch(SHOW_SNACK, {
            message: this.$t("STOCK.INVOICES.MIXED_SELECTION_NOT_ALLOWED"),
            color: "warning",
          });
          return;
        }

        this.selectedSelectionKind = kind;
        this.selectedActionIds = [...this.selectedActionIds, id];
        this.$set(item, "selected", true);
        return;
      }

      this.selectedActionIds = this.selectedActionIds.filter((value) => value !== id);
      this.$set(item, "selected", false);
      if (this.selectedActionIds.length === 0) {
        this.selectedSelectionKind = null;
      }
    },
    clearSelection() {
      this.selectedActionIds = [];
      this.selectedSelectionKind = null;
      const table = this.$refs["stock-movements-table"];
      if (table && table.serverItems) {
        table.serverItems.forEach((item) => this.$set(item, "selected", false));
      }
    },
    openBulkInvoiceDialog() {
      if (!this.selectedActionIds.length) {
        return;
      }
      this.showInvoiceLinkDialog = true;
    },
    handleBulkInvoiceLinkConfirm(options) {
      if (options.openOnly && options.stock_invoice_id) {
        this.showInvoiceLinkDialog = false;
        this.clearSelection();
        this.$router.push({
          name: "stock-invoices-view",
          params: { id: options.stock_invoice_id },
        });
        return;
      }
      this.bulkCreateInvoice(options);
    },
    handleInvoiceLinkError(errors) {
      this.$store.dispatch(SHOW_SNACK, {
        message: (errors && errors.message) || this.$t("GENERAL.UNEXPECTED_ERROR"),
        color: "error",
      });
    },
    async bulkCreateInvoice(options = {}) {
      if (!this.selectedActionIds.length) {
        return;
      }
      this.isCreatingInvoice = true;
      try {
        const action =
          this.selectedSelectionKind === "receipt"
            ? CREATE_STOCK_INVOICE_FROM_RECEIPTS
            : CREATE_STOCK_INVOICE_FROM_DELIVERIES;
        const data = await this.$store.dispatch(action, {
          stock_action_ids: this.selectedActionIds,
          pass_through_extra_costs: options.pass_through_extra_costs || false,
          stock_invoice_id: options.stock_invoice_id,
        });
        this.showInvoiceLinkDialog = false;
        this.clearSelection();
        this.$router.push({ name: "stock-invoices-view", params: { id: data.id } });
      } catch (errors) {
        this.$store.dispatch(SHOW_SNACK, {
          message: (errors && errors.message) || this.$t("GENERAL.UNEXPECTED_ERROR"),
          color: "error",
        });
      } finally {
        this.isCreatingInvoice = false;
      }
    },
    statusColor(status) {
      const map = {
        draft: "grey",
        expected: "orange",
        finalized: "success",
        reversed: "purple",
        cancelled: "error",
      };
      return map[status] || "grey";
    },
    statusLabel(status) {
      const key = `STOCK.STATUSES.RECEIPT.${(status || "").toUpperCase()}`;
      return this.$te(key) ? this.$t(key) : status;
    },
    linkedInvoiceLabel(invoice) {
      if (!invoice) {
        return "-";
      }
      return invoice.code || "-";
    },
  },
};
</script>
